import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

/**
 * Live "impact meter" that translates the cart total into a number of funded
 * items (e.g. immediate access beds) and how much more is needed for the next one.
 *
 * Rendered server-side first (so it's correct without JS), then kept in sync
 * client-side: it listens for `cart:update` (fired by both add-to-cart and
 * quantity changes) and re-renders every instance on the page from the
 * authoritative cart total.
 */

/** @type {Set<CsImpactMeter>} */
const instances = new Set();
let listenerAttached = false;

/**
 * Refreshes every meter on the page from the current cart total.
 * Uses the event payload when it includes a total, otherwise fetches /cart.js.
 * @param {CustomEvent} [event]
 */
async function refreshAll(event) {
  let total = event?.detail?.resource?.total_price;

  if (typeof total !== 'number') {
    try {
      const cartRoute = typeof Theme !== 'undefined' ? Theme.routes?.cart_url : null;
      const cartUrl = `${cartRoute || '/cart'}.js`;
      const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      total = cart.total_price;
    } catch (error) {
      return;
    }
  }

  if (typeof total !== 'number') return;
  instances.forEach((instance) => instance.render(total));
}

class CsImpactMeter extends HTMLElement {
  connectedCallback() {
    instances.add(this);

    if (!listenerAttached) {
      document.addEventListener(ThemeEvents.cartUpdate, refreshAll);
      listenerAttached = true;
    }
  }

  disconnectedCallback() {
    instances.delete(this);
  }

  /**
   * Re-renders the message and progress bar for a given cart total.
   * @param {number} totalPence - The cart total in minor currency units.
   */
  render(totalPence) {
    const unit = parseInt(this.dataset.unit || '4000', 10) || 4000;
    const moneyFormat = this.dataset.moneyFormat || '£{{amount}}';
    const currency = this.dataset.currency || 'GBP';
    const singular = this.dataset.singular || '';
    const plural = this.dataset.plural || '';

    const count = Math.floor(totalPence / unit);
    const within = totalPence % unit;
    const remaining = within === 0 ? unit : unit - within;
    const progress = Math.min(100, Math.round((within / unit) * 100));
    const item = count === 1 ? singular : plural;

    const remainingMoney = formatMoney(remaining, moneyFormat, currency);
    const totalMoney = formatMoney(totalPence, moneyFormat, currency);

    const template = count >= 1 ? this.dataset.progressText || '' : this.dataset.zeroText || '';
    const message = template
      .replaceAll('{count}', String(count))
      .replaceAll('{item}', item)
      .replaceAll('{singular}', singular)
      .replaceAll('{plural}', plural)
      .replaceAll('{remaining}', remainingMoney)
      .replaceAll('{total}', totalMoney);

    const messageEl = this.querySelector('[data-impact-message]');
    if (messageEl) messageEl.innerHTML = message;

    const barEl = this.querySelector('[data-impact-bar]');
    if (barEl) {
      barEl.style.setProperty('--cs-impact-progress', `${progress}%`);
      barEl.setAttribute('aria-valuenow', String(progress));
    }

    this.dataset.count = String(count);
  }
}

if (!customElements.get('cs-impact-meter')) {
  customElements.define('cs-impact-meter', CsImpactMeter);
}
